import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Box,
  Button,
  Checkbox,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'

import { useIntl } from 'react-intl'
import { ReactComponent as Edit } from '../../../assets/icons/edit.svg'
import { ReactComponent as Delete } from '../../../assets/icons/trash.svg'
import useTranslations from '../../../i18n/useTranslations'
import EnhancedTableHead from './EnhancedTableHead'
import EnhancedTableToolbar from './EnhancedTableToolbar'
import {
  AGENDA_TABLE_HEADER_ORDER,
  AGENDA_TABLE_HEAD_CELLS,
} from '../../../common/constants'
import { useStyles } from '../styles'
import ModalLayout from '../../../ui/modals/layout'
import * as routes from '../../../routes'
import { Link } from 'react-router-dom/cjs/react-router-dom'
import styles from './styles.js'

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  return stabilizedThis.map(el => el[0])
}

const DEFAULT_PAGING_PARAMS = {
  page: 0,
  rowsPerPage: 10,
  totalRows: 28,
}

const AgendaTable = ({
  onChangeTableParams,
  defaultSortType = 'desc',
  rows,
  orderLineId = 'orderLineId',
  handleSubmitDelete,
  onClickEditContact,
  selectedRows = [],
  tableParams = DEFAULT_PAGING_PARAMS,
  rowsPerPageOptions = [5, 10, 25, 50],
  showCheckbox,
  idHolder = 'id',
  onClickDeleteContact,
  showMenu,
  onSelectAllClick = () => {},
  onCheckboxChange = () => {},
}) => {
  const t = useTranslations()
  const intl = useIntl()
  const classes = useStyles()

  const [deleteFile, setDeleteFile] = useState(null)
  const [selected, setSelected] = useState([])
  const [anchorEls, setAnchorEls] = useState(null)
  const anchorRefs = useRef([])

  const { page, rowsPerPage, totalRows } = tableParams

  const showLabelDisplayedRows = () => {
    if (!totalRows) return null
    const indexInitRow = (page + 1) * rowsPerPage - rowsPerPage + 1
    const indexLastRow = Math.min(indexInitRow + rowsPerPage - 1, totalRows)
    return `${indexInitRow}-${indexLastRow} de ${totalRows}`
  }

  const handleRequestSort = (event, sortColumn) => {
    const getOppositeSortType = sortType =>
      sortType === 'asc' ? 'desc' : 'asc'

    onChangeTableParams({
      ...tableParams,
      sortColumn,
      sortType:
        sortColumn === tableParams.sortColumn
          ? getOppositeSortType(tableParams.sortType)
          : defaultSortType,
    })
  }

  // eslint-disable-next-line
  const handleChangePage = (event, newPage) => {
    onChangeTableParams({ ...tableParams, page: newPage })
  }

  const handleChangeRowsPerPage = event => {
    onChangeTableParams({
      ...tableParams,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    })
  }

  const handleSelectAllClick = event => {
    const rowIds = rows.map(n => n.data[orderLineId] ?? n.data[idHolder])
    let nextSelected = selected.filter(selected => !rowIds.includes(selected))
    if (event.target.checked) {
      nextSelected = nextSelected.concat(rowIds)
    }
    setSelected(nextSelected)
    onSelectAllClick(!event.target.checked, rows)
  }

  const handleClick = id => {
    setAnchorEls(id)
  }

  const handleClose = id => {
    setAnchorEls(null)
  }

  return (
    <>
      <ModalLayout
        onClose={() => setDeleteFile(null)}
        isOpen={deleteFile ?? false}
        actions={
          <>
            <Button
              type="button"
              size="small"
              variant="outlined"
              onClick={() => setDeleteFile(null)}
            >
              {t('action:cancel')}
            </Button>
            <Button
              type="submit"
              size="small"
              onClick={() => {
                handleSubmitDelete(deleteFile?.file_id)
                setDeleteFile(null)
              }}
            >
              {t('action:confirm')}
            </Button>
          </>
        }
        content={
          <>
            <Typography variant="body2" className={classes.paragraphText}>
              {t('shipping-recording:delete-file-body1')}
            </Typography>
            <Typography variant="body2">
              {t('shipping-recording:delete-file-body2', {
                shippingTracking: (
                  <Link
                    to={routes.SHIPPINGS_TRACKING.path}
                    style={{ color: 'red' }}
                  >
                    {t('shipping-tracking:title')}
                  </Link>
                ),
              })}
            </Typography>
          </>
        }
        title={t('shipping-recording:delete-file')}
      />
      <TableContainer sx={{ ...styles.tableCollapse, mb: 2 }}>
        <MuiTable aria-labelledby="tableTitle" aria-label="enhanced table">
          <EnhancedTableHead
            headCells={AGENDA_TABLE_HEAD_CELLS}
            order={tableParams.sortType}
            orderBy={tableParams.sortColumn}
            onRequestSort={handleRequestSort}
            numSelected={selectedRows.length}
            showCheckbox={showCheckbox}
            onSelectAllClick={handleSelectAllClick}
            rowCount={rows.length}
            rows={rows}
            selectedRows={selectedRows}
          />
          <TableBody>
            {stableSort(
              rows,
              getComparator(tableParams.sortType, tableParams.sortColumn),
            ).map((row, index) => {
              const rowInfo = row?.data
              const id = rowInfo?.id
              const selectedRow = !!selectedRows.find(
                ({ id }) => id === rowInfo?.id,
              )
              return (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={index}>
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={id}
                    className={clsx(row.details?.severity, {
                      'is-selected': selectedRow,
                    })}
                  >
                    {showCheckbox ? (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRow}
                          onChange={e => onCheckboxChange(rowInfo)}
                        />
                      </TableCell>
                    ) : null}
                    {AGENDA_TABLE_HEADER_ORDER?.map((key, index) =>
                      key === 'contact-client-code' && index === 0 ? (
                        <TableCell className="main" key={key}>
                          {row?.data['contactClientCode']}
                        </TableCell>
                      ) : key === 'name-company' ? (
                        <TableCell className="main" key={key}>
                          {row?.data['name']}
                        </TableCell>
                      ) : key === 'phone' ? (
                        <TableCell className="main" key={key}>
                          {row?.data['phone']?.replace(/\s/g, "")}
                        </TableCell>
                      ) : key === 'address' ? (
                        <TableCell className="main" key={key}>
                          {t('address:without-name', {
                            address: row?.data.address,
                            postalCode: row?.data.postalCode,
                            city: row?.data.city,
                            country: intl.formatDisplayName(row?.data.country, {
                              type: 'region',
                            }),
                          })}
                        </TableCell>
                      ) : (
                        <TableCell key={key}>{row?.data[key]}</TableCell>
                      ),
                    )}
                    {showMenu ? (
                      <>
                        <TableCell classes={{ root: 'icon' }}>
                          <Edit onClick={()=>onClickEditContact(row?.data)} className={classes.pointer}/>
                        </TableCell>
                        <TableCell classes={{ root: 'icon' }}>
                          <Delete onClick={()=>onClickDeleteContact(row?.data)} className={classes.pointer} />
                        </TableCell>
                        {/* <TableCell classes={{ root: 'icon' }}>
                          <Fav className={classes.actionButtons} />
                        </TableCell> */}
                      </>
                    ) : null}
                  </TableRow>
                </React.Fragment>
              )
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {true ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
          py={2}
          px={1}
        >
          <EnhancedTableToolbar numTotal={totalRows} numSelected={0} />
          <TablePagination
            component="div"
            rowsPerPageOptions={rowsPerPageOptions}
            labelRowsPerPage={t('noun:contacts-per-page')}
            labelDisplayedRows={showLabelDisplayedRows}
            count={totalRows ?? 1}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      ) : null}
    </>
  )
}

AgendaTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.oneOfType([
        PropTypes.shape({
          numero_colegiado: PropTypes.string,
          nombre: PropTypes.string,
          apellidos: PropTypes.string,
          modalidades: PropTypes.string,
          farmacia: PropTypes.string,
          telefono: PropTypes.string,
          correo_electronico: PropTypes.string,
        }),
        PropTypes.shape({
          nombre: PropTypes.string,
          telefono: PropTypes.string,
          correo_electronico: PropTypes.string,
          estado: PropTypes.string,
        }),
        PropTypes.shape({
          year: PropTypes.string,
          day: PropTypes.string,
          reason: PropTypes.string,
          type: PropTypes.string,
          locality: PropTypes.string,
        }),
      ]),
    }),
  ),
  tableParams: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalRows: PropTypes.number,
    sortType: PropTypes.string,
    sortColumn: PropTypes.string,
  }),
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.string),
  onChangeTableParams: PropTypes.func,
  selectedRows: PropTypes.arrayOf(PropTypes.string),
  defaultSortType: PropTypes.oneOf(['desc', 'asc']),
  handleSubmitDelete: PropTypes.func,
  showCheckbox: PropTypes.bool,
  orderLineId: PropTypes.string,
  idHolder: PropTypes.string,
}

export default AgendaTable
